import { ColumnDef } from '@tanstack/react-table';
import { Link } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import { Trash2, FilePen } from 'lucide-react';
import { CompanyDocumentQueryData } from '@area-restrita/api-client/model/companyDocumentQueryData';
import { useDeleteCompanyDocument } from '@area-restrita/api-client/endpoints/company-document/company-document.gen';

import { Button } from '@/components/ui/button';
import Dialog from '@/components/template/Dialog';
import { DialogFooter } from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';
import QRCodeDialog from '../../components/qrcode-dialog';

export const columns: ColumnDef<CompanyDocumentQueryData>[] = [
  {
    id: 'search',
    meta: { className: 'hidden' },
  },
  { id: 'name', header: 'Nome', accessorKey: 'name' },
  {
    id: 'categoryIds',
    header: 'Categoria',
    accessorFn: ({ documentCategory }) =>
      documentCategory?.description ?? 'N/A',
  },
  {
    id: 'companyIds',
    header: 'Empresa',
    accessorFn: ({ companyName, isAllCompanies, totalCompanies }) => {
      if (isAllCompanies) return 'Todas as empresas';

      if (totalCompanies > 1) return `${totalCompanies} empresas`;

      return companyName ?? 'N/A';
    },
  },
  {
    id: 'updatedAt',
    header: 'Atualizado em',
    accessorFn: ({ updatedAt }) =>
      format(parseISO(updatedAt), 'HH:mm dd/MM/yyyy'),
  },
  {
    id: 'actions',
    header: 'Ações',
    meta: {
      className: 'text-right',
      onClick: (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>) =>
        e.stopPropagation(),
    },
    cell: function CompanyDocumentsActionCell({ row }) {
      const { checkAuthorization, getUserType } = useAuthSessionStore();
      const canDelete = checkAuthorization(({ can }) =>
        can('delete', 'documents'),
      );

      const { mutateAsync: deleteCompanyDocument, status } =
        useDeleteCompanyDocument();

      return (
        <div className="flex justify-end gap-3">
          <QRCodeDialog
            documentName={row.original.name}
            documentPublicId={row.original.publicId}
          />

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/documents/$companyDocumentPublicId/request-changes"
                  params={{ companyDocumentPublicId: row.original.publicId }}
                >
                  <Button variant="outline" size="icon-sm">
                    <FilePen className="h-4 w-4" />
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>Solicitar alteração de documento</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {getUserType() === 'tenant' && (
            <Dialog
              title="Excluir Documento"
              trigger={
                <Button
                  variant="destructive"
                  size="icon-sm"
                  disabled={!canDelete}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              }
            >
              <p>
                Tem certeza que deseja excluir o documento "{row.original.name}
                "?
              </p>

              <DialogFooter>
                <Button
                  variant="destructive"
                  size="sm"
                  loading={status === 'pending'}
                  onClick={() =>
                    deleteCompanyDocument({ id: row.original.publicId })
                  }
                >
                  Excluir
                </Button>
              </DialogFooter>
            </Dialog>
          )}
        </div>
      );
    },
  },
];

export const hiddenColumnsOnCompany = ['companyIds', 'actions'];
export const companyColumns: ColumnDef<CompanyDocumentQueryData>[] = [
  ...columns.filter(
    (column) => !hiddenColumnsOnCompany.includes(column.id ?? ''),
  ),
  {
    id: 'actions',
    header: 'Ações',
    meta: {
      className: 'text-right',
      onClick: (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>) =>
        e.stopPropagation(),
    },
    cell: function CompanyDocumentsActionCell({ row }) {
      return (
        <div className="flex justify-end gap-3">
          <QRCodeDialog
            documentName={row.original.name}
            documentPublicId={row.original.publicId}
          />
        </div>
      );
    },
  },
];
